import { useHasAccess } from '@components/HasAccess'
import Page from '@components/Page/Page'
import { Venue } from '@components/venue/Venue'
import { TableUrlParams, useTableUrlSync } from '@hooks/useTableUrlSync'
import { BusinessVerifier as BusinessVerifierModel } from '@models/business/BusinessVerifier'
import {
  getBusinessVerifiers,
  selectBusinessVerifiers,
  setBusinessVerifierQuery,
} from '@store/business/BusinessVerifierReducer'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { SBTable, TableParams } from '@supplement-bacon/sb-table-filters'
import { SBRM, SBRMAction, useSBRM } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import { Permission } from '@utils/permissions'
import { type TableColumnsType } from 'antd'
import { useEffect } from 'react'

const BusinessVerifierPage = () => {
  const { hasAccess } = useHasAccess()
  const { isOpen } = useSBRM()
  const dispatch = useAppDispatch()
  const businessVerifiers = useAppSelector(selectBusinessVerifiers())
  const { query, isLoading } = useAppSelector((state) => state.businessVerifier)

  const columns: TableColumnsType<BusinessVerifierModel> = [
    {
      title: 'Venue',
      render: (record: BusinessVerifierModel) => (
        <Venue.Avatar id={record.venue} size="small" />
      ),
    },
    {
      title: 'Username',
      dataIndex: 'username',
    },
    {
      title: 'POS Config',
      render: (record: BusinessVerifierModel) => (
        <Venue.DominoConfig id={record.venue} />
      ),
    },
    {
      align: 'right',
      render: (record: BusinessVerifierModel) => (
        <SBRM.Actions
          actions={[SBRMAction.Update, SBRMAction.Delete]}
          entity={Entity.businessVerifier}
          entityId={record.id}
          disabled={!hasAccess(Permission.manageBusiness)}
        />
      ),
    },
  ]

  const fetchBusinessVerifiers = (params: TableUrlParams) => {
    const newQuery: TableParams = {
      ...query,
      pagination: {
        ...query.pagination,
        current: params.page,
        pageSize: params.pageSize,
      },
    }

    dispatch(setBusinessVerifierQuery(newQuery))
    dispatch(getBusinessVerifiers(newQuery))
  }

  const { handleTableChange, params } = useTableUrlSync<BusinessVerifierModel>({
    onParamsChange: fetchBusinessVerifiers,
  })

  useEffect(() => {
    if (isOpen) return

    fetchBusinessVerifiers(params)
  }, [isOpen])

  return (
    <Page
      permissions={Permission.manageBusiness}
      pageTitle={{
        title: 'Verifier accounts',
        level: 4,
        toolbar: (
          <SBRM.Actions
            actions={[SBRMAction.Create]}
            entity={Entity.businessVerifier}
            disabled={!hasAccess(Permission.manageBusiness)}
          />
        ),
      }}
    >
      <SBTable
        columns={columns}
        dataSource={businessVerifiers}
        loading={isLoading}
        onChange={handleTableChange}
        pagination={query.pagination}
      />
    </Page>
  )
}

export default BusinessVerifierPage
